import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Typography, Popover, Box, LinearProgress, Button, Divider, Alert, Stepper, StepLabel, Step, TextField } from '@vapor/material'
import { Skeleton, StepIconProps, SvgIcon } from '@mui/material'
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { HiCIcon } from 'src/app/components/icons/icon'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { ReservationHeaderComponentsCommonProps } from '../header'
import { EditableBox, TypographyCode } from '../header-components.styled'
import { faCreditCard, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { getPortalIcon, getPortalName } from 'src/utils/portal-utils'
import {
  PortalInfoFragment,
  PortalName,
  PortalPaymentMethod,
  ReferenceCustomerFragment,
  TsPayLinkType,
  useCreateLinkFroReservationMutation,
  useCustomerUpdateMutation,
  useGetReservationPortalPaymentCardQuery,
  useMessageTemplatesAutomationQuery,
  useSendReservationEmailMutation,
} from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { tsPayStepType, PaymentInfoStatusType } from './payment-info.types'
import { useReservationInfo } from './payment-info.hook'
import { FormItem, useConfirm } from 'hic-components'
import { isEmail } from 'src/utils/contact'
import { useSnackbar } from 'notistack'
import { cleanTypename } from 'src/utils/typename-cleaner'
import { ChargeOnCreditCard } from './charge-on-cc'
import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { filterMessageTemplate } from 'src/app/pages/settings/mail-templates/mail-template-list/filter-message-template'

export const PaymentInfo = React.memo(
  function PaymentInfo({ reservation }: ReservationHeaderComponentsCommonProps) {
    const ref = useRef(null)
    const { t } = useTranslation()
    const formatDate = useFormatDate()
    const confirm = useConfirm()

    const [customerUpdate] = useCustomerUpdateMutation()

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

    const closePopover = useCallback(() => {
      setAnchorEl(null)
    }, [])

    const disablePaymentRequest = useMemo(() => {
      return (reservation.reservationState.canceled || reservation.reservedResources?.every((rr) => rr.phase === 'CHECKOUT')) ?? false
    }, [reservation])

    const { reservationOrigin, paymentInfoVisible, paymentSteps, lastPaymentStepTodo, paymentStatus, tsPayReqestType, chargeOnLink2Save } = useReservationInfo(reservation)

    const [paymentInfoSteps, setPaymentInfoStep] = useState<tsPayStepType[]>(paymentSteps)
    const [paymentInfoStepTodo, setPaymentInfoStepTodo] = useState<number>(lastPaymentStepTodo)
    const [paymentInfoStatus, setPaymentInfoStatus] = useState<PaymentInfoStatusType>('ND')

    const [showTsPayForm, setShowTsPayForm] = useState(false)

    useEffect(() => {
      setPaymentInfoStatus(paymentStatus)
    }, [paymentStatus])

    const statusColor = useMemo(() => {
      switch (paymentInfoStatus) {
        case 'PENDING':
          return 'warning'
        case 'COMPLETED':
          return 'success'
        case 'ERROR':
          return 'error'
        case 'ND':
          return 'secondary'
      }
      return 'warning'
    }, [paymentInfoStatus])

    const getDefaultEmail = (customer: ReferenceCustomerFragment) => {
      const defaultEmail = customer.mailContacts?.filter((m) => m.isDefault)
      if (defaultEmail && defaultEmail?.length > 0) {
        return defaultEmail[0].value
      }
      return ''
    }

    const getReservationEmailReference = useCallback(() => {
      return getDefaultEmail(reservation.reference.mainGuest)
    }, [reservation])

    const executeCustomerUpdate = (customer: ReferenceCustomerFragment, tenantId: string) => {
      const cleanCustomer = cleanTypename<ReferenceCustomerFragment>(customer)
      const { id, auditable, company, ...customerInput } = { ...cleanCustomer }
      customerUpdate({
        variables: {
          id: customer.id,
          input: { ...customerInput, tenantId: tenantId },
        },
      })
    }

    const updateCustomerEmail = useCallback(
      (address: string) => {
        const customer = { ...reservation.reference.mainGuest }
        const customerEmail = getDefaultEmail(customer)
        if (customerEmail === '') {
          // Update without ask
          const updCustomer: ReferenceCustomerFragment = { ...customer, mailContacts: [{ isDefault: true, order: 1, type: 'MAIN', value: address }] }
          executeCustomerUpdate(updCustomer, reservation.tenantId)
        } else {
          // Ask if want update
          const updCustomer: ReferenceCustomerFragment = { ...customer, mailContacts: [...(customer.mailContacts?.map((m) => (m.isDefault ? { ...m, value: address } : m)) ?? [])] }
          confirm({
            title: t('pms.reservation.payment_info.update_email_confirm_title'),
            message: t('pms.reservation.payment_info.update_email_confirm_message', { customerName: customer.name }),
            confirmButtonText: t('commons.ok'),
            cancelButtonText: t('commons.cancel'),
            onConfirm: () => executeCustomerUpdate(updCustomer, reservation.tenantId),
          })
        }
      },
      [reservation],
    )

    return (
      <>
        {paymentInfoVisible ? (
          <div ref={ref} style={{ flexGrow: 1 }}>
            <EditableBox onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ width: 'fit-content' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIcon color={statusColor} fontSize="small">
                  <FontAwesomeIcon icon={faCreditCard} />
                </SvgIcon>
                <Typography variant="caption">
                  <b>{t('pms.reservation.portal_info')}</b>
                </Typography>
              </Stack>
            </EditableBox>
            <Popover
              open={anchorEl ? true : false}
              anchorEl={anchorEl}
              onClose={closePopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                sx: {
                  minWidth: '250px',
                },
              }}
            >
              <Stack padding={2} spacing={1}>
                {reservationOrigin === 'OTA' ? (
                  reservation.portalInfo ? (
                    <PortalInfoContents tenantId={reservation.tenantId} reservationId={reservation.id} portalInfo={reservation.portalInfo} />
                  ) : undefined
                ) : (
                  <>
                    {!showTsPayForm && paymentInfoSteps.length > 0 ? ( //
                      <>
                        <TsPayInfoContents //
                          steps={paymentInfoSteps}
                          lastStep={paymentInfoStepTodo}
                          onErrorClick={() => {
                            setShowTsPayForm(true)
                          }}
                        />
                        {chargeOnLink2Save.allowChargeOnLink2Save && !chargeOnLink2Save.alreadyCharged ? (
                          <>
                            <Divider />
                            <ChargeOnCreditCard
                              propertyId={reservation.propertyId[0]}
                              reservationId={reservation.id}
                              maxAmountAllowed={chargeOnLink2Save.maxAmountAllowed}
                              description={t('pms.booking.reservation.chare_for_reservation.execute.transaction_default_description', { reservation: reservation.reservationCode })}
                              onCreationComplete={closePopover}
                              refetchQueries={['ReservationDetail']}
                            />
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    ) : (
                      <>
                        {
                          tsPayReqestType && (
                            <TsPayRequestForm //
                              tsPayReqestType={tsPayReqestType}
                              emailAddress={getReservationEmailReference()}
                              reservationId={reservation.id}
                              tenantId={reservation.tenantId}
                              propertyId={reservation.propertyId[0]}
                              onEmailSent={(to) => {
                                const infoStep = [
                                  ...paymentInfoSteps,
                                  {
                                    description:
                                      tsPayReqestType === 'LINK2PAY' ? t('pms.reservation.payment_info.payment_request_sent_step') : t('pms.reservation.payment_info.cc_guarantee_request_step'),
                                    date: formatDate(new Date(), 'Pp'),
                                  },
                                  { description: t('pms.reservation.payment_info.payment_request_pending_step') },
                                ]
                                setPaymentInfoStep(infoStep)
                                setPaymentInfoStepTodo(infoStep.length - 1)
                                setPaymentInfoStatus('PENDING')

                                updateCustomerEmail(to)
                                if (showTsPayForm) {
                                  setShowTsPayForm(false)
                                }
                              }}
                              disabled={disablePaymentRequest}
                            />
                          ) // TODO: al momento diamo per scontato che ci sia una sola property. Da analizzare nel momento in cui gestiremo prenotazioni su più property.
                        }
                      </>
                    )}
                  </>
                )}
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent={'end'} p={1}>
                <Button size="small" variant="contained" onClick={() => closePopover()}>
                  {t('commons.close')}
                </Button>
              </Stack>
            </Popover>
          </div>
        ) : (
          <></>
        )}
      </>
    )
  },
  (prev, next) =>
    JSON.stringify(prev.reservation.portalInfo) === JSON.stringify(next.reservation.portalInfo) &&
    JSON.stringify(prev.reservation.origin) === JSON.stringify(next.reservation.origin) &&
    JSON.stringify(prev.reservation.tsPayLinks) === JSON.stringify(next.reservation.tsPayLinks),
)

type ImgPortalProps = {
  portalName?: PortalName
  size?: 'small' | 'medium'
}

type CreditCardInfoProps = {
  tenantId: string
  reservationId: string
  onCountDownEnd: () => void
}

const GetCreditCardInfo = ({ tenantId, reservationId, onCountDownEnd }: CreditCardInfoProps) => {
  const {
    loading,
    error,
    data: ccData,
  } = useGetReservationPortalPaymentCardQuery({
    variables: {
      tenantId,
      reservationId,
    },
    fetchPolicy: 'no-cache',
  })

  const { t } = useTranslation()
  const showError = useErrorSnackBar()

  const [countdown, setCountdown] = useState(100)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null
    if (isActive) {
      timer = setInterval(() => {
        setCountdown((countdown) => {
          if (countdown > 0) return countdown - 2
          else {
            setIsActive(false)
            onCountDownEnd()
            return countdown
          }
        })
      }, 400)
    }
    return () => {
      if (timer !== null) clearInterval(timer)
    }
  }, [isActive])

  useEffect(() => {
    if (!loading) setIsActive(true)
  }, [loading])

  useEffect(() => {
    if (error) {
      showError(error?.message)
      onCountDownEnd()
    }
  }, [error])

  const cardFormatter = (number: string) => {
    const s: string[] = []
    const parts = Math.floor(number.length / 4)
    for (let x = 0; x < parts; x++) {
      const sP = x * 4
      const eP = sP + 4 > number.length ? number.length : sP + 4
      s.push(number.substring(sP, eP))
    }
    return s
  }

  if (loading)
    return (
      <Box display="flex" height={80} alignItems="center" justifyContent={'center'}>
        <Typography variant="caption">{t('commons.loading')}</Typography>
      </Box>
    )

  if (ccData?.getReservationPortalPaymentCard) {
    const { expiration, fullName, network, number, cvv } = ccData.getReservationPortalPaymentCard

    // // DATI FAKE PER PROVARE IL FUNZIONAMENTO DEL COMPONENTE
    // if (!ccData?.getReservationPortalPaymentCard) {
    //   const expiration = '05/26'
    //   const fullName = 'ROSSI GIOVANNI MARIO'
    //   const network = 'VISA'
    //   const number = '1234123412341234'
    //   const cvv = '123'
    return (
      <>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <TypographyCode>{network}</TypographyCode>
          <TypographyCode>
            {cardFormatter(number).map((chunk, i) => (
              <span key={i} style={{ paddingLeft: '4px' }}>
                {chunk}
              </span>
            ))}
          </TypographyCode>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Stack>
            <Typography variant="captionSmall">{t('pms.reservation.portal_ingo.cc.full_name').toUpperCase()}</Typography>
            <TypographyCode sx={{ marginRight: 'auto' }}>{fullName.toUpperCase()}</TypographyCode>
          </Stack>
          <Stack>
            <Typography variant="captionSmall">{t('pms.reservation.portal_ingo.cc.exp').toUpperCase()}</Typography>
            <TypographyCode>{expiration}</TypographyCode>
          </Stack>
          <Stack>
            <Typography variant="captionSmall">CVV</Typography>
            <TypographyCode>{cvv ? cvv : ''}</TypographyCode>
          </Stack>
        </Stack>
        <LinearProgress color={'primary'} sx={{ height: 6 }} variant="determinate" value={countdown} />
      </>
    )
  } else {
    return <></>
  }
}

type PortalInfoContentsProps = {
  tenantId: string
  reservationId: string
  portalInfo: PortalInfoFragment
}

const PortalInfoContents = ({ tenantId, reservationId, portalInfo }: PortalInfoContentsProps) => {
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()
  const { t } = useTranslation()
  const isFromAirbnb = portalInfo.portal.name === 'AIRBNB'

  const [isActive, setIsActive] = useState(false)

  const getPaymentMethod = (paymentMethod: PortalPaymentMethod | null | undefined) => {
    if (paymentMethod) {
      switch (paymentMethod) {
        case 'VIRTUAL_CARD':
        case 'CUSTOMER_CREDIT_CARD':
        case 'CASH':
        case 'BANK_TRANSFER':
        case 'NONE':
          return t(`pms.reservation.portal_info.payment_method.${paymentMethod}`)
        default:
          return ''
      }
      return ''
    }
  }

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent={'space-between'} alignItems="strech">
        {portalInfo?.payment?.amount ? (
          <Stack justifyContent={'space-between'}>
            <Typography variant="titleSmall">{isFromAirbnb ? t('pms.reservation.portal_info.title_charged_amount_airbnb') : t('pms.reservation.portal_info.title_charged_amount')}</Typography>
            <Stack direction="row" spacing={1}>
              <Typography variant="body">
                <strong>{formatCurrency(portalInfo?.payment?.amount ?? 0, portalInfo?.payment?.currency ?? currency)}</strong>
              </Typography>
              {!isFromAirbnb && (
                <Typography variant="caption">{portalInfo?.payment?.commissionModel && t(`pms.reservation.portal_info.commission_model.${portalInfo?.payment?.commissionModel}`)}</Typography>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography variant="titleSmall">{t('pms.reservation.portal_info')}</Typography>
        )}
        <Stack alignItems={'end'}>
          <Stack direction="column" alignItems={'center'}>
            <span>{portalInfo?.portal.name && <ImgPortal portalName={portalInfo?.portal.name} size="medium" />}</span>
            <Typography variant="caption" align="right">
              <b>{getPortalName(portalInfo?.portal.name)}</b>
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {portalInfo?.payment?.currency && portalInfo?.payment?.currency !== currency && (
        <Alert severity="warning" variant="outlined">
          {t('pms.reservation.portal_info.currency_alert')}
        </Alert>
      )}
      <Divider light />
      {portalInfo.payment?.paymentMethod && (
        <Stack direction="row" spacing={1} justifyContent={'space-between'}>
          <Typography variant="caption">{t('pms.reservation.portal_info.payment_method.label')}</Typography>
          <Typography variant="caption">
            <b>{getPaymentMethod(portalInfo?.payment?.paymentMethod)}</b>
          </Typography>
        </Stack>
      )}
      {portalInfo?.payment?.paymentCard?.cardActivationDate && (
        <Stack direction="row" spacing={1} justifyContent={'space-between'}>
          <Typography variant="caption">{t('pms.reservation.portal_info.card_activation_date')}</Typography>
          <Typography variant="caption">
            <b>{formatDate(portalInfo?.payment?.paymentCard?.cardActivationDate, 'P p')}</b>
          </Typography>
        </Stack>
      )}
      {[PortalPaymentMethod.VIRTUAL_CARD.toString(), PortalPaymentMethod.CUSTOMER_CREDIT_CARD.toString()].includes(portalInfo?.payment?.paymentMethod ?? '') ||
      (!portalInfo.payment?.paymentMethod && portalInfo.payment?.paymentCard) ? (
        <>
          <Divider light />

          <Button variant="text" size="small">
            <Stack direction="row" justifyContent={'center'} spacing={1} alignItems="center" onClick={() => setIsActive((x) => !x)}>
              <HiCIcon>
                <FontAwesomeIcon icon={isActive ? faLockOpen : faLock} />
              </HiCIcon>
              <Typography>{isActive ? t('pms.reservation.portal_info.hide_card_info') : t('pms.reservation.portal_info.show_card_info')}</Typography>
            </Stack>
          </Button>
          {!isActive ? (
            <Stack rowGap={0}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Skeleton height={24} animation={false} width="15%"></Skeleton>
                <Skeleton height={24} animation={false} width="85%"></Skeleton>
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Skeleton height={44} animation={false} width="50%"></Skeleton>
                <Skeleton height={44} animation={false} width="20%"></Skeleton>
                <Skeleton height={44} animation={false} width="20%"></Skeleton>
              </Stack>
              <Skeleton height={8} animation={false} width="100%"></Skeleton>
            </Stack>
          ) : (
            <GetCreditCardInfo //
              reservationId={reservationId}
              tenantId={tenantId}
              onCountDownEnd={() => setIsActive(false)}
            />
          )}
        </>
      ) : undefined}
    </>
  )
}

type TsPayInfoContentsProps = {
  steps: tsPayStepType[]
  lastStep: number
  onErrorClick: () => void
}

function StepIcon({ error }: StepIconProps) {
  return (
    <FontAwesomeIcon
      icon={error ? faXmark : faCheck}
      style={{
        border: `2px solid ${error ? 'var(--error)' : 'var(--RichElectricBlue)'}`,
        height: '32px',
        width: '32px',
        padding: '4px',
        color: error ? 'var(--error)' : 'var(--RichElectricBlue)',
        boxSizing: 'border-box',
      }}
    />
  )
}

const TsPayInfoContents = ({ steps, lastStep, onErrorClick }: TsPayInfoContentsProps) => {
  const { t } = useTranslation()

  return (
    <Stepper orientation="vertical" activeStep={lastStep}>
      {steps?.map((s, i) => (
        <Step key={`STEP_${i}`}>
          <StepLabel error={Boolean(s.error)} StepIconComponent={StepIcon}>
            <Stack>
              {s.description}
              <Typography variant="caption">{s.date}</Typography>
            </Stack>
          </StepLabel>
          {Boolean(s.error) && i === lastStep && (
            <Button variant="contained" onClick={onErrorClick} size="small">
              {t('pms.reservation.payment_info.retry')}
            </Button>
          )}
        </Step>
      ))}
    </Stepper>
  )
}

type TsPayRequestFromProps = {
  emailAddress: string
  reservationId: string
  tenantId: string
  propertyId: string
  onEmailSent: (to: string) => void
  tsPayReqestType: TsPayLinkType
  disabled: boolean
}

const TsPayRequestForm = ({ emailAddress, reservationId, tenantId, propertyId, onEmailSent, tsPayReqestType, disabled }: TsPayRequestFromProps) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const showError = useErrorSnackBar()

  const [sendReservationEmail] = useSendReservationEmailMutation({
    onError: (error) => {
      showError(error)
      setLoading(false)
    },
  })
  const [createPaymentLinkForReservation] = useCreateLinkFroReservationMutation()

  const [email, setEmail] = useState<string>(emailAddress)
  const [loading, setLoading] = useState<boolean>(false)
  const isValidEmail = (Boolean(email) && isEmail(email)) ?? false

  const { data: link2PayTemplatesData } = useMessageTemplatesAutomationQuery({
    variables: {
      filter: {
        customOnly: false,
        isActive: true,
        tenantId: { eq: tenantId },
        code: { eq: 'LINK2PAY' },
        type: 'MAIL',
      },
    },
    onError: showError,
  })
  const link2PayEmailTemplate = (link2PayTemplatesData?.messageTemplates ?? []).filter((messageTemplate) => {
    return filterMessageTemplate(messageTemplate, link2PayTemplatesData?.messageTemplates ?? [])
  })[0]

  const sendEmail = useCallback(async () => {
    if (!link2PayEmailTemplate) {
      return showError(t('pms.send_communication_email.no_communication_template', { template_code: t('pms.message_template_code.LINK2PAY') }))
    }

    setLoading(true)
    const result = await sendReservationEmail({
      variables: {
        input: {
          tenantId: tenantId,
          propertyId: propertyId,
          reservationId: reservationId,
          sendTo: email,
          messageTemplateId: link2PayEmailTemplate.id,
        },
      },
    })
    setLoading(false)
    if (!result?.errors) onEmailSent(email)
  }, [emailAddress, email, reservationId, tenantId, propertyId, onEmailSent, link2PayEmailTemplate?.id])

  const [paymentLink2Share, setPaymentLink2Share] = useState<ShareData>({
    title: tsPayReqestType === 'LINK2SAVE' ? t('pms.reservation.payment_info.copy_link_title_LINK2SAVE') : t('pms.reservation.payment_info.copy_link_title_LINK2PAY'),
  })

  const generateLinkClick = useCallback(async () => {
    setLoading(true)

    const link2Share = { ...paymentLink2Share }

    // Generate link if not exists
    if (!link2Share?.url) {
      const result = await createPaymentLinkForReservation({
        variables: {
          tenantId: tenantId,
          propertyId: propertyId,
          reservationId: reservationId,
        },
        onError: (error) => console.error(error),
      })
      setLoading(false)
      const url = result.data?.createLinkForReservation?.url
      if (url) {
        link2Share.url = url
        setPaymentLink2Share({ ...paymentLink2Share, url: url })
      }
    }

    // Copy link to clipboard
    if (link2Share.url) {
      const canShareLink = navigator?.canShare && navigator?.canShare() && navigator.canShare(link2Share)

      if (canShareLink) {
        navigator.share(link2Share)
      } else {
        navigator.clipboard.writeText(link2Share.url)
        enqueueSnackbar(t('pms.payment.tsPay.link2pay.copy_link_confirm'), {
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        })
      }
    }
  }, [reservationId, tenantId, propertyId, paymentLink2Share])

  return (
    <Stack spacing={1}>
      <Typography variant="titleSmall">
        {tsPayReqestType === 'LINK2PAY' ? t('pms.reservation.payment_info.send_email_title_LINK2PAY') : t('pms.reservation.payment_info.send_email_title_LINK2SAVE')}
      </Typography>
      {disabled ? (
        <Alert variant="outlined" severity="warning">
          {t('pms.reservation.payment_info.send_email.disabled')}
        </Alert>
      ) : (
        <>
          <FormItem fullWidth errorMessage={!isValidEmail ? t('commons.required_field') : undefined} fieldLabel={t('commons.email')}>
            <TextField fullWidth value={email} onChange={(event) => setEmail(event.target.value)}></TextField>
          </FormItem>
          <Button variant="contained" onClick={() => sendEmail()} disabled={loading}>
            {t('pms.reservation.payment_info.send_email_button')}
          </Button>
          <Divider light />

          <Typography variant="caption">{t('pms.reservation.payment_info.copy_link_info')}</Typography>
          <Button variant="outlined" onClick={() => generateLinkClick()} disabled={loading}>
            {t('pms.reservation.payment_info.copy_link_button')}
          </Button>
          {paymentLink2Share.url && (
            <Typography variant="caption" color={'var(--success)'}>
              {t('pms.reservation.payment_info.copy_link_success')}
            </Typography>
          )}
        </>
      )}
    </Stack>
  )
}

export const ImgPortal = React.memo(
  function ImgPortal(props: ImgPortalProps) {
    const portalIcon = useMemo(() => {
      if (props.portalName) return getPortalIcon(props.portalName)
      return undefined
    }, [props.portalName])

    const iconSize = useMemo(() => {
      if (props.size === 'medium') return 32
      return 16
    }, [props.size])

    if (portalIcon) return <img src={portalIcon} alt={getPortalName(props.portalName)} width={iconSize} />
    else return <></>
  },
  (prev, next) => JSON.stringify(prev.portalName) === JSON.stringify(next.portalName),
)
